import { EventEmitter } from 'events';
import { EventType, } from './types';
import { getModuleLogger } from '@utils/logger';
const logger = getModuleLogger('chat-sdk');
export default class StreamProcessor extends EventEmitter {
    isPaused = false;
    hasEnded = false;
    isFirstResponse = true;
    accumulatedContent = '';
    abortController = null;
    stream = null;
    constructor(abortController) {
        super();
        this.abortController = abortController || null;
        logger.debug('[StreamProcessor] 初始化', { hasAbortController: !!abortController });
    }
    /**
     * 类型安全的事件监听
     */
    on(event, listener) {
        return super.on(event, listener);
    }
    /**
     * 类型安全的单次事件监听
     */
    once(event, listener) {
        return super.once(event, listener);
    }
    /**
     * 发出事件（检查 isPaused 状态）
     */
    emitEvent(type, data) {
        if (this.isPaused) {
            logger.debug('[StreamProcessor.emitEvent] 已暂停，忽略事件', { type });
            return;
        }
        logger.debug('[StreamProcessor.emitEvent] 发出事件', { type, hasData: !!data });
        this.emit(type, data);
    }
    /**
     * 手动终止流处理
     */
    async stop() {
        logger.info('[StreamProcessor.stop] 停止流处理');
        // 先发出 Pause 事件，再设置 isPaused（否则 emitEvent 会被拦截）
        this.emit(EventType.Pause);
        this.isPaused = true;
        // 中断 fetch 请求
        if (this.abortController) {
            this.abortController.abort();
            logger.debug('[StreamProcessor.stop] 已中断 fetch 请求');
        }
        // 取消 openai stream
        if (this.stream) {
            this.stream.controller.abort();
            logger.debug('[StreamProcessor.stop] 已取消 openai stream');
        }
    }
    /**
     * 处理 openai Stream
     */
    async processStream(stream) {
        if (this.isPaused) {
            logger.debug('[StreamProcessor.processStream] 已暂停，跳过处理');
            return;
        }
        logger.info('[StreamProcessor.processStream] 开始处理流');
        this.stream = stream;
        try {
            let chunkCount = 0;
            for await (const chunk of stream) {
                if (this.isPaused) {
                    logger.debug('[StreamProcessor.processStream] 处理过程中被暂停', { chunkCount });
                    break;
                }
                chunkCount += 1;
                this.processChunk(chunk);
            }
            logger.info('[StreamProcessor.processStream] 流处理完成', { totalChunks: chunkCount });
            // 流正常结束，如果还没发送 End 事件，发送一个
            this.emitEndEvent();
        }
        catch (e) {
            if (!this.isPaused) {
                const err = e instanceof Error ? e : new Error(String(e));
                // 忽略 abort 错误
                if (err.name !== 'AbortError') {
                    logger.error('[StreamProcessor.processStream] 流处理错误', err);
                    this.emitEvent(EventType.Error, err);
                }
                else {
                    logger.debug('[StreamProcessor.processStream] 流处理被中止');
                }
            }
        }
    }
    /**
     * 处理单个 chunk
     */
    processChunk(chunk) {
        const { choices, usage, model } = chunk;
        const choice = choices?.[0];
        if (choice) {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const { delta, finish_reason } = choice;
            const extendedDelta = delta;
            const content = extendedDelta?.content || extendedDelta?.reasoning_content;
            // 第一个 SSE 回包（只有 role，没有 content）
            if (extendedDelta?.role === 'assistant' && !content) {
                if (this.isFirstResponse) {
                    this.isFirstResponse = false;
                    logger.debug('[StreamProcessor.processChunk] 收到第一个响应（Init）', { model });
                    const initData = {
                        isNew: true,
                    };
                    this.emitEvent(EventType.Init, initData);
                }
                return;
            }
            // 最后一个 SSE 回包
            if (finish_reason) {
                logger.debug('[StreamProcessor.processChunk] 收到结束信号', { finish_reason, usage });
                this.emitEndEvent(usage);
                return;
            }
            // 正常的数据包
            if (typeof content === 'string') {
                this.accumulatedContent += content;
                const dataEvent = {
                    delta: content,
                    model,
                };
                logger.log('[StreamProcessor.processChunk] 收到数据块', {
                    deltaLength: content.length,
                    accumulatedLength: this.accumulatedContent.length,
                });
                this.emitEvent(EventType.Data, dataEvent);
            }
        }
        else if (usage) {
            // 只有 usage 信息的情况
            logger.debug('[StreamProcessor.processChunk] 收到 usage 信息', { usage });
            this.emitEndEvent(usage);
        }
        else {
            // 兜底：第一个响应
            if (this.isFirstResponse) {
                this.isFirstResponse = false;
                logger.debug('[StreamProcessor.processChunk] 兜底：第一个响应（Init）');
                this.emitEvent(EventType.Init, {
                    isNew: true,
                });
            }
        }
    }
    /**
     * 发送 End 事件
     */
    emitEndEvent(usage) {
        // 防止重复发送 End 事件
        if (this.hasEnded) {
            logger.debug('[StreamProcessor.emitEndEvent] 已结束，跳过重复发送');
            return;
        }
        this.hasEnded = true;
        const promptTokens = usage?.prompt_tokens || 0;
        const completionTokens = usage?.completion_tokens || 0;
        const endData = {
            value: this.accumulatedContent,
            promptTokens,
            completionTokens,
        };
        logger.info('[StreamProcessor.emitEndEvent] 流处理结束', {
            contentLength: this.accumulatedContent.length,
            promptTokens,
            completionTokens,
        });
        this.emitEvent(EventType.End, endData);
    }
}
